/* components/patterns/Cards.jsx — карточки-плитки каталогов + каркас виджета.
   IngredientGridCard / RecipeCard / MenuGridCard / DocumentCard
   WidgetCard — каркас виджета аналитики (loading/error/empty/content) + Excel-экспорт.
   SkeletonGrid — сетка skeleton-карточек загрузки каталогов.
   MiniBarChart — лёгкий SVG bar-chart (ЭКВИВАЛЕНТ recharts для прототипа).
   Карточки получают данные ИЗВНЕ (пропсы) — не импортируют моки (03 §13.3).
   ХЕНДОФФ → frontend/components/<module>/ (доменные) + common/ (WidgetCard, SkeletonGrid). */

import React from 'react';
import { DocumentCard } from './DocumentCard.jsx';
import { FoodCostBadge, KindBadge, Pill, StatusBadge, StatusPill } from '../ui/Badge.jsx';
import { Kebab } from '../ui/Buttons.jsx';
import { ChipList } from '../ui/Chip.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Popover } from '../ui/Popover.jsx';
import { ExtraMetric } from '../ui/Metric.jsx';
import { LoadingState, EmptyState, ErrorState, ForbiddenState } from './States.jsx';
import { fmt } from '../../lib/format.js';
import { useToast } from '../../app/providers/ToastProvider.jsx';

  const { useState } = React;

  // Единый паттерн активации кликабельной карточки с клавиатуры (Enter/Space).
  // Карточки рендерятся как <article role="button"> → роль button требует onKeyDown.
  function cardKeyActivate(onClick) {
    if (!onClick) return undefined;
    return (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        onClick(e);
      }
    };
  }

  /* ── IngredientGridCard ── */
  function IngredientGridCard({ item = {}, onClick, menu, className, selectable, selected, onToggleSelect }) {
    const hasPrice = item.amount_rub != null;
    const activate = selectable ? onToggleSelect : onClick;
    return (
      <article className={'tile tile--ingredient' + (selected ? ' tile--selected' : '') + (className ? ' ' + className : '')}
        onClick={activate} tabIndex={activate ? 0 : undefined}
        role={activate ? 'button' : undefined} onKeyDown={cardKeyActivate(activate)}
        aria-label={activate && item.name ? item.name : undefined}>
        <div className="tile__top tile__top--menu-only">
          <KindBadge kind="ingredient" label={item.type_label} size="sm" />
          {selectable
            ? <input type="checkbox" className="tile__select" checked={!!selected} readOnly
                aria-label={'Выбрать «' + (item.name || '') + '»'}
                onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(); }} />
            : menu}
        </div>
        <h3 className="tile__title t-h4 clamp-2">{item.name}</h3>
        <div className="tile__price">
          {hasPrice ? (
            <>
              <span className="num tile__price-val">{fmt.money(item.amount_rub)}</span>
              {item.unit_label && <span className="text-mute"> / {item.unit_label}</span>}
            </>
          ) : (
            <Pill tone="warn" size="sm">Нет цены</Pill>
          )}
        </div>
        {item.supplier_name && (
          <div className="tile__meta t-caption truncate"><Icon name="building" size={13} /> {item.supplier_name}</div>
        )}
      </article>
    );
  }

  /* ── RecipeCard (ТТК / полуфабрикат) ──
     F41: selectable/selected/onToggleSelect — режим multi-select реестра
     (bulk-операции): клик по карточке тогда переключает выбор, не открывает. */
  function RecipeCard({ item = {}, onClick, menu, className, selectable, selected, onToggleSelect }) {
    const activate = selectable ? onToggleSelect : onClick;
    return (
      <article className={'tile tile--recipe' + (selected ? ' tile--selected' : '') + (className ? ' ' + className : '')}
        onClick={activate} tabIndex={activate ? 0 : undefined}
        role={activate ? 'button' : undefined} onKeyDown={cardKeyActivate(activate)}
        aria-label={activate && item.name ? item.name : undefined}>
        <div className="tile__top">
          <div className="cluster cluster-4">
            <KindBadge kind={item.kind || 'dish'} size="sm" />
            {item.status && <StatusBadge status={item.status} size="sm" />}
          </div>
          {selectable
            ? <input type="checkbox" className="tile__select" checked={!!selected} readOnly
                aria-label={'Выбрать «' + (item.name || '') + '»'}
                onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(); }} />
            : menu}
        </div>
        <h3 className="tile__title t-h4 clamp-2">{item.name}</h3>
        <div className="tile__metrics cluster cluster-12">
          <ExtraMetric label="Ккал" value={item.kcal != null ? fmt.kcal(item.kcal) : '—'} />
          <ExtraMetric label="Выход" value={item.yield_g != null ? fmt.grams(item.yield_g) : '—'} />
          <ExtraMetric label="Себест." value={item.cost_rub != null ? fmt.money(item.cost_rub) : '—'} />
        </div>
        {item.allergens && item.allergens.length > 0 && (
          <div className="tile__allergens"><ChipList items={item.allergens} max={3} /></div>
        )}
      </article>
    );
  }

  /* ── MenuGridCard ── */
  function MenuGridCard({ item = {}, onClick, menu, className, selectable, selected, onToggleSelect }) {
    const activate = selectable ? onToggleSelect : onClick;
    return (
      <article className={'tile tile--menu' + (selected ? ' tile--selected' : '') + (className ? ' ' + className : '')}
        onClick={activate} tabIndex={activate ? 0 : undefined}
        role={activate ? 'button' : undefined} onKeyDown={cardKeyActivate(activate)}
        aria-label={activate && item.name ? item.name : undefined}>
        <div className="tile__top">
          <div className="cluster cluster-4">
            <KindBadge kind="menu" size="sm" />
            {item.status && <StatusBadge status={item.status} size="sm" />}
          </div>
          {selectable
            ? <input type="checkbox" className="tile__select" checked={!!selected} readOnly
                aria-label={'Выбрать «' + (item.name || '') + '»'}
                onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(); }} />
            : menu}
        </div>
        <h3 className="tile__title t-h4 clamp-2">{item.name}</h3>
        <div className="tile__metrics cluster cluster-12">
          <ExtraMetric label="Блюд" value={item.dishes != null ? item.dishes : '—'} />
          <ExtraMetric label="Ср. чек" value={item.avg_check != null ? fmt.money(item.avg_check) : '—'} />
          <ExtraMetric label="Ср. себест." value={item.avg_cost != null ? fmt.money(item.avg_cost) : '—'} />
        </div>
        {item.fc != null && <div className="tile__fc"><FoodCostBadge value={item.fc} /></div>}
      </article>
    );
  }

  /* ── DocumentTile ── плитка документа в реестре (// заменяет: S1 DocumentCard).
     Полнофункциональная карточка базы знаний — отдельный S9 `DocumentCard`
     (components/patterns/DocumentCard.jsx, перекрывает DocumentCard ниже). */
  const FILE_ICON = { pdf: 'file-text', doc: 'file-text', img: 'image', xls: 'file', default: 'file' };
  function DocumentTile({ item = {}, onClick, menu, className }) {
    const processing = item.summary_status === 'processing';
    return (
      <article className={'doc-card' + (className ? ' ' + className : '')}>
        <div className="doc-card__head">
          <span className="doc-card__icon"><Icon name={FILE_ICON[item.filetype] || FILE_ICON.default} size={20} /></span>
          <div className="grow" style={{ minWidth: 0 }}>
            <h3 className="doc-card__name t-label truncate">{item.name}</h3>
            <div className="doc-card__meta t-caption">{item.meta}</div>
          </div>
          {menu}
        </div>
        <div className="doc-card__summary">
          {processing
            ? <div className="doc-card__processing t-caption"><Icon name="spinner" size={14} className="icon--spin" /> Резюме готовится…</div>
            : <p className="t-body-sm text-dim clamp-2">{item.summary || 'Резюме не сформировано.'}</p>}
        </div>
      </article>
    );
  }

  /* ── MiniBarChart (recharts-эквивалент) ── */
  function MiniBarChart({ data = [], height = 140, color = 'var(--accent)' }) {
    const max = Math.max(1, ...data.map((d) => d.value));
    return (
      <div className="mini-chart" style={{ height }}>
        {data.map((d, i) => (
          <div className="mini-chart__col" key={i}>
            <div className="mini-chart__bar" style={{ height: (d.value / max * 100) + '%', background: color }}>
              <span className="mini-chart__tip num">{d.value}</span>
            </div>
            <span className="mini-chart__label t-caption truncate">{d.label}</span>
          </div>
        ))}
      </div>
    );
  }

  /* ── WidgetCard ──
     Состояния тела рендерятся каноническими State-компонентами (States.jsx),
     а не форк-хромом — единые иконки/размеры/тексты. `skeleton` (опц.) даёт
     per-тип skeleton тела на загрузке (как KPI-бар) вместо общего спиннера. */
  function WidgetCard({ title, subtitle, actions, onExport, loading, error, forbidden, empty, emptyLabel, onRetry, skeleton, children, className, help }) {
    const toast = useToast();
    const [exporting, setExporting] = useState(false);
    // Скачивание — async (серверный ?export=xlsx): пока качается — спиннер,
    // ошибка — тост, а не unhandled rejection (раньше кнопка молча «не работала»).
    const handleExport = async () => {
      if (!onExport || exporting) return;
      setExporting(true);
      try {
        await onExport();
      } catch {
        toast?.show('Не удалось скачать файл. Попробуйте ещё раз.', 'danger');
      } finally {
        setExporting(false);
      }
    };
    let body;
    if (loading) body = skeleton || <LoadingState />;
    else if (forbidden) body = <ForbiddenState body="Нет доступа к данным виджета." />;
    else if (error) body = <ErrorState title="Не удалось загрузить" body="Данные виджета недоступны. Попробуйте ещё раз." onRetry={onRetry} />;
    else if (empty) body = <EmptyState icon="analytics" serif={false} title={emptyLabel || 'Нет данных за период'} />;
    else body = children;
    return (
      <section className={'widget-card' + (className ? ' ' + className : '')}>
        <header className="widget-card__head">
          <div className="grow" style={{ minWidth: 0 }}>
            <h3 className="widget-card__title t-h4 truncate">{title}</h3>
            {subtitle && <div className="widget-card__sub t-caption truncate">{subtitle}</div>}
          </div>
          <div className="widget-card__actions cluster cluster-4">
            {actions}
            {help && (
              <Popover placement="bottom-end" width={280}
                trigger={(
                  <button type="button" className="icon-btn-mini" aria-label="Об отчёте" title="Об отчёте">
                    <Icon name="info" size={16} />
                  </button>
                )}>
                <div className="widget-help">{help}</div>
              </Popover>
            )}
            {onExport && (
              <button type="button" className="icon-btn-mini" onClick={handleExport} disabled={exporting} aria-label="Скачать Excel" title="Скачать Excel">
                <Icon name={exporting ? 'spinner' : 'download'} size={16} className={exporting ? 'icon--spin' : undefined} />
              </button>
            )}
          </div>
        </header>
        <div className="widget-card__body">{body}</div>
      </section>
    );
  }

  /* ── SkeletonGrid ── */
  function SkeletonGrid({ count = 6, aspect = '1.3', className }) {
    return (
      <div className={'tile-grid' + (className ? ' ' + className : '')}>
        {Array.from({ length: count }).map((_, i) => (
          <div className="tile tile--skeleton" key={i}>
            <div className="cluster cluster-4"><span className="skeleton" style={{ width: 64, height: 18, borderRadius: 999 }} /></div>
            <span className="skeleton" style={{ width: '80%', height: 18 }} />
            <span className="skeleton" style={{ width: '60%', height: 14 }} />
            <span className="skeleton" style={{ width: '40%', height: 14 }} />
          </div>
        ))}
      </div>
    );
  }

export { IngredientGridCard };
export { RecipeCard };
export { MenuGridCard };
export { DocumentTile };
export { WidgetCard };
export { SkeletonGrid };
export { MiniBarChart };
